import { dateInZone } from '../../shared/datetime';
import type { ExportType, ReportBasis } from '../../shared/crm';

// UK business dates follow London regardless of the browser timezone,
// matching the server's crmToday().
export const crmToday = () => dateInZone(new Date(), 'Europe/London');

export function addDaysISO(value: string, days: number): string {
  const date = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function formatMoney(value: string | number | null | undefined, currency = '£'): string {
  if (value == null || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value);
  return `${currency}${amount.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatDays(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—';
  const days = Number(value);
  if (!Number.isFinite(days)) return String(value);
  return `${days.toLocaleString('en-GB', { maximumFractionDigits: 1 })}d`;
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function crmDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function crmDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Binary endpoints are fetched with session cookies by top-level navigation.
export const documentHref = (id: string) => `/api/crm/documents/${id}/content`;
export const invoicePdfHref = (id: string) => `/api/crm/invoices/${id}/pdf`;
export function exportHref(type: ExportType, from: string, to: string, basis?: ReportBasis) {
  const query = new URLSearchParams({ type, from, to });
  if (basis) query.set('basis', basis);
  return `/api/crm/reports/export?${query}`;
}

export const CRM_NAV = [
  { href: '/crm', label: 'Dashboard' },
  { href: '/crm/follow-ups', label: 'Follow-ups' },
  { href: '/crm/freelancers', label: 'Freelancers' },
  { href: '/crm/engagements', label: 'Engagements & SOWs' },
  { href: '/crm/compliance', label: 'Compliance' },
  { href: '/crm/capacity', label: 'Capacity' },
  { href: '/crm/time', label: 'Time & expenses' },
  { href: '/crm/invoices', label: 'Invoices' },
  { href: '/crm/payments', label: 'Payments' },
  { href: '/crm/recurring', label: 'Recurring' },
  { href: '/crm/reports', label: 'Tax & reports' },
  { href: '/crm/settings', label: 'Settings' },
] as const;

const SECTION_LABELS: Record<string, string> = {
  'follow-ups': 'Follow-ups',
  freelancers: 'Freelancers',
  engagements: 'Engagements & SOWs',
  compliance: 'Compliance',
  capacity: 'Capacity',
  time: 'Time & expenses',
  invoices: 'Invoices',
  payments: 'Payments',
  recurring: 'Recurring',
  reports: 'Tax & reports',
  settings: 'CRM settings',
};

/** Breadcrumb label for the main app topbar; the CRM subnav shows context. */
export function crmPageLabel(path: string): string {
  if (path === '/crm' || path === '/crm/') return 'CRM';
  return SECTION_LABELS[path.split('/')[2] ?? ''] ?? 'CRM';
}
